import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';

import { useClientes, useSituacaoDoMes } from '../../shared/api/queries.ts';
import type {
  Cliente,
  FiltroDaSituacaoMensal,
  ResultadoEnvioPorEmail,
  SituacaoMensalDoCliente,
} from '../../shared/api/tipos.ts';
import { competenciaPorExtenso, formatarData, formatarInteiro } from '../../shared/format.ts';
import {
  periodoDaCompetencia,
  SeletorDeCompetencia,
} from '../../shared/ui/SeletorDeCompetencia.tsx';
import { useSessao } from '../auth/sessao.tsx';
import { AvisoDeEnvio } from '../envios/AvisoDeEnvio.tsx';
import { DialogoEnvioPorEmail } from '../envios/DialogoEnvioPorEmail.tsx';

/** O que o combo oferece; "todas" não vira filtro nenhum. */
type Escolha = 'todas' | FiltroDaSituacaoMensal;

const OPCOES: readonly { valor: Escolha; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'com_pendencia', rotulo: 'Com pendência' },
  { valor: 'nao_enviado', rotulo: 'Não enviados' },
  { valor: 'enviado', rotulo: 'Enviados' },
  { valor: 'falhou', rotulo: 'Falhou' },
  { valor: 'sem_notas', rotulo: 'Sem notas' },
];

/**
 * Como está cada loja no mês escolhido: o que saiu e o que espera alguém.
 *
 * O eixo é a competência, e não o acervo inteiro, porque é assim que o
 * escritório trabalha — fecha-se um mês de cada vez. A coluna do envio e as
 * de pendência são duas escalas diferentes de propósito: uma loja pode ter o
 * mês entregue e ainda ter nota sem resposta da SEFAZ, e juntar as duas numa
 * coluna só esconderia uma delas.
 *
 * **O envio do mês sai daqui**, na linha de cada loja: é o lugar onde quem
 * decide tem na frente o que está mandando — quantas notas, se já saiu antes
 * e quantas ainda esperam a SEFAZ. Na tela de arquivos fiscais o botão vivia
 * ao lado de um filtro de chave e número, longe de qualquer uma dessas
 * respostas. A pendência vira aviso dentro do diálogo e não trava nada: a
 * decisão continua sendo de quem olha.
 */
export function SituacaoDoMes({
  competencia,
  aoMudarCompetencia,
  situacao,
  aoMudarSituacao,
  aoEscolherCliente,
}: {
  competencia: string;
  aoMudarCompetencia: (competencia: string) => void;
  situacao: Escolha;
  aoMudarSituacao: (situacao: Escolha) => void;
  aoEscolherCliente: (empresaId: string) => void;
}) {
  const { usuario } = useSessao();
  // O contador não manda e-mail: ele é o destinatário. O botão sai da tela
  // dele porque a rota recusa — mandar documento fiscal em nome do
  // escritório é do escritório.
  const daEquipe = usuario?.papel !== 'contador';

  const { data, isPending, isError, error } = useSituacaoDoMes(
    competencia,
    situacao === 'todas' ? undefined : situacao,
  );
  // O diálogo precisa do cadastro do cliente (e-mails e contabilidade), que
  // a linha do mês não carrega: ela responde pelo movimento, não pelo
  // cadastro.
  const clientes = useClientes();

  const [enviando, setEnviando] = useState<SituacaoMensalDoCliente | null>(null);
  const [enviado, setEnviado] = useState<ResultadoEnvioPorEmail | null>(null);

  const itens = data?.itens ?? [];
  const resumo = data?.resumoDoMes;
  const periodo = periodoDaCompetencia(competencia);
  const clienteDoEnvio: Cliente | null =
    enviando === null
      ? null
      : (clientes.data?.itens.find((item) => item.id === enviando.empresaId) ?? null);

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'flex-start' } }}
          >
            <SeletorDeCompetencia
              valor={competencia}
              aoMudar={aoMudarCompetencia}
              ajuda="A tela abre no mês que se está fechando."
            />

            <TextField
              select
              label="Situação"
              value={situacao}
              onChange={(evento) => aoMudarSituacao(evento.target.value as Escolha)}
              sx={{ minWidth: 200 }}
              helperText="Com pendência é outra pergunta: ela convive com o mês enviado."
            >
              {OPCOES.map((opcao) => (
                <MenuItem key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Os contadores são do mês inteiro, não do filtro: trocar o
              recorte da tabela não muda como o mês está. */}
          {resumo !== undefined && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 2, gap: 1 }}>
              <Chip size="small" label={`${formatarInteiro(resumo.enviados)} enviados`} color="success" />
              <Chip
                size="small"
                label={`${formatarInteiro(resumo.naoEnviados)} não enviados`}
                color={resumo.naoEnviados > 0 ? 'warning' : 'default'}
              />
              {resumo.falhas > 0 && (
                <Chip size="small" color="error" label={`${formatarInteiro(resumo.falhas)} com falha`} />
              )}
              <Chip
                size="small"
                variant="outlined"
                color={resumo.comPendencia > 0 ? 'warning' : 'default'}
                label={`${formatarInteiro(resumo.comPendencia)} com pendência`}
              />
              <Chip size="small" variant="outlined" label={`${formatarInteiro(resumo.semNotas)} sem notas`} />
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {isError ? (
            <Alert severity="error">
              Não foi possível carregar o mês. {error instanceof Error ? error.message : ''}
            </Alert>
          ) : isPending ? (
            <Stack spacing={1}>
              {Array.from({ length: 4 }, (_, indice) => (
                <Skeleton key={indice} variant="rounded" height={44} />
              ))}
            </Stack>
          ) : itens.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              Nenhum cliente neste recorte em {competenciaPorExtenso(competencia)}.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Notas</TableCell>
                    <TableCell align="right">Pendentes</TableCell>
                    <TableCell align="right">Rejeitadas</TableCell>
                    <TableCell>Envio do mês</TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((item) => (
                    <TableRow
                      key={item.empresaId}
                      hover
                      // A linha inteira abre a loja na aba das notas: a
                      // pergunta seguinte é sempre "quais notas?".
                      onClick={() => aoEscolherCliente(item.empresaId)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Stack>
                            <Typography variant="body2">{item.razaoSocial}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.cnpjFormatado}
                            </Typography>
                          </Stack>
                          {/* Cliente inativado aparece quando tem nota no
                              mês: ela não deixou de existir, e o período que
                              compõe continua sob guarda. */}
                          {!item.ativa && <Chip size="small" variant="outlined" label="Inativo" />}
                        </Stack>
                      </TableCell>

                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.notas === 0 ? '—' : formatarInteiro(item.notas)}
                      </TableCell>

                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.pendentes > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={formatarInteiro(item.pendentes)}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.rejeitadas > 0 ? (
                          <Chip
                            size="small"
                            color="error"
                            label={formatarInteiro(item.rejeitadas)}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <ChipDoEnvio item={item} />
                        {/* O que ficou para trás fica embaixo, não em coluna
                            própria: ele não é deste mês, mas quem fecha
                            precisa saber que existe. */}
                        {item.pendentesEmOutrosMeses > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {formatarInteiro(item.pendentesEmOutrosMeses)} em outros meses
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={(evento) => {
                            evento.stopPropagation();
                            aoEscolherCliente(item.empresaId);
                          }}
                        >
                          Ver XMLs
                        </Button>
                      </TableCell>

                      <TableCell align="right">
                        {daEquipe && (
                          <Tooltip
                            title={
                              item.notas === 0
                                ? 'Sem nota no mês: não há o que mandar.'
                                : `Mandar o link de ${competenciaPorExtenso(competencia)} para os e-mails do cadastro`
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={item.notas === 0}
                                onClick={(evento) => {
                                  evento.stopPropagation();
                                  setEnviado(null);
                                  setEnviando(item);
                                }}
                                aria-label={`Enviar o mês de ${item.razaoSocial} por e-mail`}
                              >
                                <ForwardToInboxOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {enviado !== null && <AvisoDeEnvio envio={enviado} aoFechar={() => setEnviado(null)} />}

      {enviando !== null && (
        <DialogoEnvioPorEmail
          cliente={clienteDoEnvio}
          filtro={{ empresaId: enviando.empresaId, de: periodo.de, ate: periodo.ate }}
          quantidade={enviando.notas}
          pendentes={enviando.pendentes + enviando.rejeitadas}
          aoFechar={() => setEnviando(null)}
          aoEnviado={setEnviado}
        />
      )}
    </Stack>
  );
}

/** O estado do envio em uma peça: cor, rótulo e, na falha, o motivo. */
function ChipDoEnvio({ item }: { item: SituacaoMensalDoCliente }) {
  if (item.situacao === 'enviado') {
    const quando = item.enviadoEm === null ? '' : ` ${formatarData(item.enviadoEm)}`;
    return (
      <Tooltip
        title={
          item.origemDoEnvio === 'manual'
            ? 'Enviado pela tela de arquivos fiscais.'
            : 'Enviado pelo fechamento automático.'
        }
      >
        <Chip size="small" color="success" label={`Enviado${quando}`} />
      </Tooltip>
    );
  }

  if (item.situacao === 'falhou') {
    return (
      <Tooltip title={item.erroDoEnvio ?? 'Falha sem motivo informado.'}>
        <Chip size="small" color="error" label="Falhou" />
      </Tooltip>
    );
  }

  if (item.situacao === 'sem_notas') {
    return <Chip size="small" variant="outlined" label="Sem notas" />;
  }

  return <Chip size="small" color="warning" variant="outlined" label="Não enviado" />;
}
